import { apiSlice } from "../../../features/api/apiSlice";

export type Admin = {
  id: string;
  user_id: number;
  department_id: number;
  designation_id: number;

  firstname: string;
  othername?: string | null;
  lastname: string;
  address_line2?: string | null;
  staff_no?: string | null;
  gender?: string | null;
  dob?: string | null;

  phone?: string | null;
  phone2?: string | null;
  work_email?: string | null;

  address_line1?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  employment_date?: string | null;
  employment_type?: string | null;
  job_title?: string | null;
  supervisor_user_id?: number | null;

  emergency_name?: string | null;
  emergency_phone?: string | null;

  nok_name?: string | null;
  nok_phone?: string | null;

  status: "active" | "inactive";

  user?: {
    id: number;
    name: string;
    email: string;
    whois: string;
    status: string;
  };
  department?: { id: number; name: string };
  designation?: { id: number; name: string };
};

export type AdminPayload = {
  email: string;
  whois: "admin";

  department_id: number;
  designation_id: number;

  firstname: string;
  othername?: string | null;
  lastname: string;

  staff_no?: string | null;
  gender?: string | null;
  dob?: string | null;
  marital_status?: string | null;
  nationality?: string | null;
  state_of_origin?: string | null;
  lga?: string | null;

  phone?: string | null;
  phone2?: string | null;
  work_email?: string | null;

  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;

  employment_date?: string | null;
  employment_type?: string | null;
  job_title?: string | null;
  supervisor_user_id?: number | null;

  emergency_name?: string | null;
  emergency_relationship?: string | null;
  emergency_phone?: string | null;
  emergency_address?: string | null;

  nok_name?: string | null;
  nok_relationship?: string | null;
  nok_phone?: string | null;
  nok_email?: string | null;

  bank_name?: string | null;
  bank_account_name?: string | null;
  bank_account_number?: string | null;
  bank_sort_code?: string | null;

  notes?: string | null;
  photo?: string | null;
};

function pick(res: any) {
  return res?.data?.data ?? res?.data ?? res;
}

// ✅ ALWAYS returns an array
function pickArray(res: any) {
  const d = pick(res);

  if (Array.isArray(d)) return d;

  // handles { status:"success", data:[...] }
  if (Array.isArray(d?.data)) return d.data;

  // handles { data:[...] }
  if (Array.isArray(res?.data)) return res.data;

  return [];
}

export const adminsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getAdmins: builder.query<Admin[], { q?: string } | void>({
      query: (arg) => {
        const q = (arg as any)?.q;
        return q ? `/admins?q=${encodeURIComponent(q)}` : "/admins";
      },
      transformResponse: (res: any) => {
        console.log("tnis is the ress", res);
        return pickArray(res);
      },

      providesTags: (result) => {
        const list = Array.isArray(result) ? result : [];
        return list.length
          ? [
              ...list.map((a) => ({ type: "Admins" as const, id: a.id })),
              { type: "Admins" as const, id: "LIST" },
            ]
          : [{ type: "Admins" as const, id: "LIST" }];
      },
    }),

    getAdminDetails: builder.query<Admin, string>({
      query: (id) => `/admins/${id}`,
      transformResponse: (res: any) => pick(res),
      providesTags: (_r, _e, id) => [{ type: "Admins" as const, id }],
    }),

    // createAdmin: builder.mutation<any, AdminPayload>({
    //   query: (body) => ({
    //     url: "/admins",
    //     method: "POST",
    //     body,
    //   }),
    //   invalidatesTags: [{ type: "Admins" as const, id: "LIST" }],
    // }),
    updateAdminOrg: builder.mutation<
      any,
      { id: string; department_id: number; designation_id: number }
    >({
      query: ({ id, ...body }) => ({
        url: `/admins/${id}/org`,
        method: "POST",
        body,
      }),
      invalidatesTags: (r, e, arg) => [
        { type: "Admins" as const, id: "LIST" },
        { type: "Admins" as const, id: arg.id },
      ],
    }),

    updateAdminPhone: builder.mutation<
      any,
      { id: string; phone?: string | null; phone2?: string | null }
    >({
      query: ({ id, ...body }) => ({
        url: `/admins/${id}/phone`,
        method: "POST",
        body,
      }),
      invalidatesTags: (r, e, arg) => [
        { type: "Admins" as const, id: "LIST" },
        { type: "Admins" as const, id: arg.id },
      ],
    }),

    updateAdminEmail: builder.mutation<any, { id: string; email: string }>({
      query: ({ id, ...body }) => ({
        url: `/admins/${id}/email`,
        method: "POST",
        body,
      }),
      invalidatesTags: (r, e, arg) => [
        { type: "Admins" as const, id: "LIST" },
        { type: "Admins" as const, id: arg.id },
      ],
    }),

    createAdmin: builder.mutation<any, FormData>({
      query: (body) => ({
        url: "/admins",
        method: "POST",
        body, // FormData
      }),
      invalidatesTags: [{ type: "Admins" as const, id: "LIST" }],
    }),

    updateAdmin: builder.mutation<
      any,
      { id: string; body: Partial<AdminPayload> }
    >({
      query: ({ id, body }) => ({
        url: `/admins/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "Admins" as const, id },
        { type: "Admins" as const, id: "LIST" },
      ],
    }),

    deleteAdmin: builder.mutation<any, string>({
      query: (id) => ({
        url: `/admins/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Admins" as const, id: "LIST" }],
    }),

    toggleAdminStatus: builder.mutation<any, string>({
      query: (id) => ({
        url: `/admins/${id}/toggle-status`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: "Admins" as const, id },
        { type: "Admins" as const, id: "LIST" },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetAdminsQuery,
  useGetAdminDetailsQuery,
  useCreateAdminMutation,
  useUpdateAdminMutation,
  useDeleteAdminMutation,
  useToggleAdminStatusMutation,
  useUpdateAdminOrgMutation,
  useUpdateAdminPhoneMutation,
  useUpdateAdminEmailMutation,
} = adminsApi;
